import React, { useEffect, useMemo, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import SuiteQuestionnaireBlock from '@/components/admin/SuiteQuestionnaireBlock';
import SuiteOpsPanel from '@/components/admin/SuiteOpsPanel';
import SuiteDocSection from '@/components/admin/SuiteDocSection';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { SuiteIcon } from '@/components/shared/SuiteIcon';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import {
  Download, Share2, CheckCircle2, ArrowLeft, Sparkles, TrendingUp, AlertTriangle,
  Loader2, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';

/**
 * TalentMap Admin — Sprint TM-B
 *
 * Two modes, ONE shared-engine data path:
 *   • LIST  (/admin/talentmap)                — lists every TalentMap assignment
 *     via `/api/admin/assignments?suite_code=talentmap`. No new endpoints.
 *   • DETAIL (/admin/talentmap/:assignmentId) — fetches `getAssignment` +
 *     `getReport` from the shared report engine and renders the existing
 *     Summary / Dimensions / Gaps / Compare tabs from the real payload.
 *
 * No mock data, no parallel engines, no custom collections.
 */

const FORMAT_PCT = (n) => (n == null ? '—' : `${Math.round(Number(n))}%`);
const FORMAT_SCORE = (n) => (n == null ? '—' : Math.round(Number(n)));

// Normalise a report-engine `scores.sections` entry into a chart-friendly row.
// The shared report payload exposes one of: {label, name, code, score, score_pct, max, weight}.
function normalizeSection(s) {
  const label = s.label || s.name || s.section_name || s.code || 'Section';
  const score = (s.score_pct ?? s.score ?? s.percentage ?? 0);
  // If scoring engine returned a 0-1 ratio, scale up to 0-100 for charting.
  const score100 = score <= 1 ? Math.round(score * 100) : Math.round(score);
  // Target is optional — many reports don't have it; fall back to the band threshold.
  const target = (s.target_pct ?? s.target ?? null);
  const target100 = target == null ? null : (target <= 1 ? Math.round(target * 100) : Math.round(target));
  return { label, score: score100, target: target100, n: s.n };
}

function statusTone(status) {
  if (!status) return 'outline';
  if (status === 'released_to_client') return 'success';
  if (status === 'approved') return 'success';
  if (status === 'under_review' || status === 'edited_by_hm') return 'info';
  return 'outline';
}


// ---------------------------------------------------------------------------
// LIST MODE
// ---------------------------------------------------------------------------


function TalentMapList({ t }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Shared assignment engine — never any custom endpoint.
        const assignments = await adminApi.listAssignments({ suite_code: 'talentmap' });
        if (cancelled) return;
        // Hydrate user + company names in two batched fetches via the shared
        // /admin/users and /admin/clients endpoints (already reused all over
        // the platform — no new endpoints introduced).
        const [users, companies] = await Promise.all([
          adminApi.listAllUsers().catch(() => []),
          adminApi.listCompanies().catch(() => []),
        ]);
        if (cancelled) return;
        const usersById = new Map(users.map((u) => [u.id, u]));
        const cosById = new Map(companies.map((c) => [c.id, c]));
        // UAT D2 — users carry department_id only; resolve names from the
        // departments catalog of each involved company (batched).
        const deptCompanyIds = [...new Set(assignments
          .map((a) => usersById.get(a.assigned_to)?.department_id && a.company_id)
          .filter(Boolean))];
        const deptLists = await Promise.all(
          deptCompanyIds.map((cid) => adminApi.listDepartments(cid).catch(() => [])));
        const deptById = new Map(deptLists.flat().map((d) => [d.id, d.name]));
        const enriched = assignments.map((a) => {
          const cand = usersById.get(a.assigned_to) || null;
          return {
            ...a,
            _candidate: cand ? { ...cand, department_name: cand.department_name || deptById.get(cand.department_id) || null } : null,
            _company: cosById.get(a.company_id) || null,
          };
        });
        enriched.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        setRows(enriched);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <Topbar
        title="TalentMap"
        subtitle={t('pages.talentMap.subtitle')}
      />

      <div className="p-6 lg:p-8 space-y-6">
        {/* Founder UAT — role-aware operational supervision layer */}
        <SuiteOpsPanel suiteCode="talentmap" />

        <SuiteQuestionnaireBlock suiteCode="talentmap" suiteName="TalentMap" />

        <Card className="p-6 shadow-card" data-testid="tm-list">
          <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
            <div>
              <h3 className="font-display font-bold text-lg tracking-tight">
                {t('pages.talentMap.list.title', { defaultValue: 'TalentMap evaluations' })}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('pages.talentMap.list.sub', {
                  defaultValue: 'Live data from the shared assignment + report engine.',
                })}
              </p>
            </div>
            <span className="text-xs text-muted-foreground" data-testid="tm-list-count">
              {rows.length}
            </span>
          </div>

          {loading && (
            <div className="py-10 flex items-center justify-center text-muted-foreground" data-testid="tm-list-loading">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {t('common.loading', { defaultValue: 'Loading…' })}
            </div>
          )}

          {!loading && rows.length === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground border border-dashed rounded-md" data-testid="tm-list-empty">
              {t('pages.talentMap.list.empty', {
                defaultValue: 'No TalentMap evaluations have been created yet.',
              })}
            </div>
          )}

          {!loading && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                    <th className="py-2 pr-3">{t('pages.talentMap.list.colEmployee', { defaultValue: 'Employee' })}</th>
                    <th className="py-2 px-3">{t('pages.talentMap.list.colCompany', { defaultValue: 'Company' })}</th>
                    <th className="py-2 px-3">{t('pages.talentMap.list.colPosition', { defaultValue: 'Position' })}</th>
                    <th className="py-2 px-3">{t('pages.talentMap.list.colDepartment', { defaultValue: 'Department' })}</th>
                    <th className="py-2 px-3">{t('pages.talentMap.list.colStatus', { defaultValue: 'Assignment status' })}</th>
                    <th className="py-2 px-3 text-center">{t('pages.talentMap.list.colProgress', { defaultValue: 'Completion %' })}</th>
                    <th className="py-2 px-3">{t('pages.talentMap.list.colReport', { defaultValue: 'Report status' })}</th>
                    <th className="py-2 px-3">{t('pages.talentMap.list.colLast', { defaultValue: 'Last activity' })}</th>
                    <th className="py-2 pl-3 text-right">{t('common.actions', { defaultValue: 'Actions' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className="border-b last:border-0 hover:bg-muted/30 transition-smooth"
                      data-testid={`tm-row-${r.id}`}
                    >
                      <td className="py-3 pr-3 font-medium">
                        {r._candidate?.name || r._candidate?.email || '—'}
                        {r.configuration && (
                          <span className="block text-xs font-normal text-muted-foreground" data-testid={`tm-row-cfg-${r.id}`}>
                            {r.configuration.label}{r.configuration_resolved_via === 'default_fallback' ? ` (${t('pages.suiteConsole.cfgFallback', { defaultValue: 'predeterminada' })})` : ''}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-muted-foreground">{r._company?.name || '—'}</td>
                      <td className="py-3 px-3 text-muted-foreground">{r.position || '—'}</td>
                      <td className="py-3 px-3 text-muted-foreground">{r._candidate?.department_name || '—'}</td>
                      <td className="py-3 px-3"><StatusBadge status={r.status} /></td>
                      <td className="py-3 px-3 text-center tabular-nums">{FORMAT_PCT(r.progress)}</td>
                      <td className="py-3 px-3">
                        {r.status === 'submitted' ? (
                          <Badge variant="outline" className="text-xs">
                            {t('pages.talentMap.list.reportAvail', { defaultValue: 'Available' })}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-xs text-muted-foreground">
                        {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString(getUiLocale()) : (r.created_at ? new Date(r.created_at).toLocaleDateString(getUiLocale()) : '—')}
                      </td>
                      <td className="py-3 pl-3 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/admin/talentmap/${r.id}`)}
                          data-testid={`tm-row-view-${r.id}`}
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          {t('pages.talentMap.list.view', { defaultValue: 'View evaluation' })}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <SuiteDocSection suiteCode="talentmap" />
      </div>
    </>
  );
}


// ---------------------------------------------------------------------------
// DETAIL MODE
// ---------------------------------------------------------------------------


function TalentMapDetail({ t, assignmentId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [attested, setAttested] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      // 1. Assignment + stub report (shared engine)
      const d = await adminApi.getAssignment(assignmentId);
      setData(d);
      // 2. Full report (shared engine) when one exists
      if (d?.report?.id) {
        const full = await adminApi.getReport(d.report.id);
        setReport(full);
      } else {
        setReport(null);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (assignmentId) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId]);

  const reportId = data?.report?.id;
  const reportStatus = report?.report?.status || data?.report?.status;
  const isReleased = reportStatus === 'released_to_client';
  // Founder UAT — release is only possible from `approved` (backend rule);
  // anything earlier must go through the report review workspace.
  const canRelease = reportStatus === 'approved';
  const needsReview = !!reportId && !isReleased && !canRelease;

  const sections = useMemo(() => {
    const arr = (report?.report?.scores?.sections) || [];
    return arr.map(normalizeSection);
  }, [report]);

  const competencies = useMemo(() => {
    const arr = (report?.report?.scores?.competencies) || [];
    return arr.map((c) => ({
      label: c.label || c.name || c.code,
      score: c.score_pct ?? c.score ?? null,
      band: c.band,
      gap: c.gap,
    }));
  }, [report]);

  const fit = report?.report?.fit || data?.report?.fit || null;
  const overall = report?.report?.scores?.overall_score ?? data?.report?.scores?.overall_score ?? null;
  const compatibility = fit?.fit_score ?? null;

  const releaseReport = async () => {
    if (!reportId) return;
    setReleasing(true);
    try {
      await adminApi.release(reportId, { human_review_confirmed: true });
      toast.success(t('pages.talentMap.detail.releaseOk', { defaultValue: 'Report released to client' }));
      setConfirmOpen(false);
      setAttested(false);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setReleasing(false); }
  };

  const downloadPdf = async () => {
    if (!reportId) return;
    setPdfBusy(true);
    try {
      const blob = await adminApi.exportReport(reportId, 'pdf');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `talentmap-${reportId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setPdfBusy(false); }
  };

  if (loading) {
    return (
      <>
        <Topbar title="TalentMap" subtitle={t('pages.talentMap.subtitle')} />
        <div className="py-16 flex items-center justify-center text-muted-foreground" data-testid="tm-detail-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          {t('common.loading', { defaultValue: 'Loading…' })}
        </div>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <Topbar title="TalentMap" subtitle={t('pages.talentMap.subtitle')} />
        <div className="p-6 lg:p-8" data-testid="tm-detail-missing">
          <Card className="p-12 text-center shadow-card">
            <p className="text-sm text-muted-foreground">
              {t('pages.talentMap.detail.notFound', { defaultValue: 'Evaluation not found.' })}
            </p>
            <Link to="/admin/talentmap" className="inline-flex items-center gap-1.5 mt-4 text-sm text-primary hover:underline">
              <ArrowLeft className="h-4 w-4" /> {t('pages.talentMap.list.back', { defaultValue: 'Back to TalentMap' })}
            </Link>
          </Card>
        </div>
      </>
    );
  }

  const a = data.assignment || {};
  const candidate = data.candidate || {};
  const company = data.company || {};
  const initials = (candidate.name || candidate.email || '?')
    .split(/\s+/).map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <>
      <Topbar
        title="TalentMap"
        subtitle={`${t('pages.talentMap.subtitle')} · ${candidate.name || candidate.email || '—'}`}
        actions={
          <div className="flex items-center gap-2">
            {reportStatus && <StatusBadge status={reportStatus} data-testid="tm-detail-report-status" />}
            <Button
              variant="outline" size="sm" className="gap-1.5"
              onClick={downloadPdf}
              disabled={!reportId || pdfBusy}
              data-testid="tm-detail-pdf"
            >
              {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {t('pages.talentMap.pdf', { defaultValue: 'Download PDF' })}
            </Button>
            {needsReview ? (
              <Button
                size="sm" variant="outline" className="gap-1.5 border-primary/40 text-primary"
                onClick={() => navigate(`/admin/reports/${reportId}`)}
                data-testid="tm-detail-review-report"
              >
                <Eye className="h-4 w-4" />
                {t('pages.talentMap.reviewReport', { defaultValue: 'Review report' })} →
              </Button>
            ) : (
              <Button
                size="sm" className="bg-gradient-primary hover:opacity-90 gap-1.5"
                onClick={() => { setAttested(false); setConfirmOpen(true); }}
                disabled={!reportId || !canRelease || releasing}
                data-testid="tm-detail-release"
              >
                {releasing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
                {isReleased
                  ? t('pages.talentMap.detail.released', { defaultValue: 'Released' })
                  : t('pages.talentMap.release', { defaultValue: 'Release to client' })}
              </Button>
            )}
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-6" data-testid="tm-detail">
        <SuiteQuestionnaireBlock suiteCode="talentmap" suiteName="TalentMap" />

        <Link
          to="/admin/talentmap"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-smooth"
          data-testid="tm-detail-back"
        >
          <ArrowLeft className="h-4 w-4" /> {t('pages.talentMap.list.back', { defaultValue: 'Back to TalentMap' })}
        </Link>

        {/* Header card */}
        <Card className="p-6 lg:p-8 shadow-card bg-gradient-to-br from-card to-muted/20" data-testid="tm-detail-header">
          <div className="flex flex-wrap items-start gap-6">
            <Avatar className="h-20 w-20 border-4 border-background shadow-soft">
              <AvatarFallback className="bg-gradient-primary text-white text-2xl font-bold">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={a.status} />
                {reportStatus && <StatusBadge status={reportStatus} />}
                {company?.name && <Badge variant="outline" className="font-medium">{company.name}</Badge>}
              </div>
              <h2 className="font-display text-3xl font-bold tracking-tight mt-3" data-testid="tm-detail-name">
                {candidate.name || candidate.email || '—'}
              </h2>
              <p className="text-muted-foreground mt-1">
                {t('pages.talentMap.candidateDash', { defaultValue: 'Candidate –' })}{' '}
                <span className="font-medium text-foreground">{a.position || data.questionnaire?.name || 'TalentMap'}</span>
                {a.submitted_at && (
                  <> · {t('pages.talentMap.evalDate', { defaultValue: 'Evaluated on' })}{' '}
                    {new Date(a.submitted_at).toLocaleDateString(getUiLocale())}</>
                )}
              </p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="font-display text-4xl font-bold text-primary" data-testid="tm-detail-overall">
                  {overall == null ? '—' : FORMAT_SCORE(overall)}
                </div>
                <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mt-1">
                  {t('pages.talentMap.overallScore', { defaultValue: 'Overall score' })}
                </div>
              </div>
              <div className="h-12 w-px bg-border" />
              <div className="text-center">
                <div className="font-display text-4xl font-bold text-accent" data-testid="tm-detail-compat">
                  {compatibility == null ? '—' : `${FORMAT_SCORE(compatibility)}%`}
                </div>
                <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mt-1">
                  {t('pages.talentMap.compatibility', { defaultValue: 'Compatibility' })}
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Tabs defaultValue="summary">
          <TabsList className="bg-muted">
            <TabsTrigger value="summary">{t('pages.talentMap.summaryTab', { defaultValue: 'Summary' })}</TabsTrigger>
            <TabsTrigger value="dimensions">{t('pages.talentMap.dimensionsTab', { defaultValue: 'Dimensions' })}</TabsTrigger>
            <TabsTrigger value="gaps">{t('pages.talentMap.gapsTab', { defaultValue: 'Strengths & gaps' })}</TabsTrigger>
            <TabsTrigger value="compare">{t('pages.talentMap.compareTab', { defaultValue: 'Compare vs target' })}</TabsTrigger>
          </TabsList>

          {/* --------------------------- SUMMARY --------------------------- */}
          <TabsContent value="summary" className="mt-5">
            <div className="grid lg:grid-cols-5 gap-6">
              <Card className="lg:col-span-3 p-6 shadow-card" data-testid="tm-detail-radar">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-display font-bold text-lg tracking-tight">
                      {t('pages.talentMap.radarTitle', { defaultValue: 'Competency profile' })}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t('pages.talentMap.radarSubtitle', { defaultValue: 'Dynamic sections from the published questionnaire' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-primary"/>{t('pages.talentMap.scoreLabel', { defaultValue: 'Score' })}</span>
                    {sections.some((s) => s.target != null) && (
                      <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-accent"/>{t('pages.talentMap.targetLabel', { defaultValue: 'Target' })}</span>
                    )}
                  </div>
                </div>
                {sections.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-12 text-center" data-testid="tm-detail-radar-empty">
                    {t('pages.talentMap.detail.noSections', {
                      defaultValue: 'No section scores available yet — the candidate has not finished the evaluation.',
                    })}
                  </p>
                ) : (
                  <div className="h-[380px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={sections} outerRadius="75%">
                        <PolarGrid stroke="hsl(var(--border))" />
                        <PolarAngleAxis dataKey="label" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                        <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                        <Radar name={t('common.score', { defaultValue: 'Score' })} dataKey="score" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.35} strokeWidth={2} />
                        {sections.some((s) => s.target != null) && (
                          <Radar name={t('common.target', { defaultValue: 'Target' })} dataKey="target" stroke="hsl(var(--accent))" fill="hsl(var(--accent))" fillOpacity={0.1} strokeWidth={2} strokeDasharray="4 4" />
                        )}
                        <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>

              <div className="lg:col-span-2 space-y-4 flex flex-col">
                <Card className="p-5 shadow-card" data-testid="tm-detail-executive">
                  <h3 className="font-display font-bold text-lg tracking-tight mb-3">
                    {t('pages.talentMap.executiveSummary', { defaultValue: 'Executive summary' })}
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {fit?.rationale
                      || data.final_recommendation?.text
                      || t('pages.talentMap.detail.noSummary', {
                        defaultValue: 'No executive summary yet — generated by HM Advisory once the candidate completes the evaluation.',
                      })}
                  </p>
                </Card>
                {fit?.verdict && (
                  <Card className="p-5 shadow-card bg-primary/5 border-primary/20">
                    <div className="flex items-start gap-3">
                      <div className="h-9 w-9 rounded-lg bg-primary text-white flex items-center justify-center shrink-0">
                        <CheckCircle2 className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm">{t('pages.talentMap.finalRec', { defaultValue: 'Final recommendation' })}</h4>
                        <p className="text-sm text-muted-foreground mt-1 capitalize">
                          {String(fit.verdict).replace(/_/g, ' ')}
                        </p>
                      </div>
                    </div>
                  </Card>
                )}
                <Card className="p-5 shadow-card mt-auto">
                  <div className="flex items-center gap-2 mb-3">
                    <SuiteIcon name="GraduationCap" color="primary" size="sm" />
                    <h4 className="font-semibold text-sm">{t('pages.talentMap.nextStep', { defaultValue: 'Next step' })}</h4>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {t('pages.talentMap.nextStepBody', {
                      defaultValue: 'You can create a TalentTrain plan from the development gaps surfaced by this evaluation.',
                    })}
                  </p>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* ------------------------- DIMENSIONS ------------------------- */}
          <TabsContent value="dimensions" className="mt-5">
            <Card className="p-6 shadow-card" data-testid="tm-detail-dimensions">
              <h3 className="font-display font-bold text-lg tracking-tight mb-5">
                {t('pages.talentMap.dimensionScores', { defaultValue: 'Section scores' })}
              </h3>
              {sections.length === 0 ? (
                <p className="text-sm text-muted-foreground py-12 text-center">
                  {t('pages.talentMap.detail.noSections', {
                    defaultValue: 'No section scores available yet — the candidate has not finished the evaluation.',
                  })}
                </p>
              ) : (
                <div style={{ height: Math.max(220, 48 * sections.length + 80) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sections} layout="vertical" margin={{ left: 30, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="label" tick={{ fontSize: 12, fill: 'hsl(var(--foreground))' }} width={180} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                      <Bar dataKey="score" fill="hsl(var(--primary))" radius={[0, 6, 6, 0]} barSize={16} />
                      {sections.some((s) => s.target != null) && (
                        <Bar dataKey="target" fill="hsl(var(--accent) / 0.4)" radius={[0, 6, 6, 0]} barSize={16} />
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* ---------------------------- GAPS ---------------------------- */}
          <TabsContent value="gaps" className="mt-5">
            <div className="grid md:grid-cols-2 gap-6" data-testid="tm-detail-gaps">
              <Card className="p-6 shadow-card">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-9 w-9 rounded-lg bg-success/10 text-success flex items-center justify-center">
                    <TrendingUp className="h-5 w-5" />
                  </div>
                  <h3 className="font-display font-bold text-lg tracking-tight">
                    {t('pages.talentMap.strengthsHdr', { defaultValue: 'Aligned strengths' })}
                  </h3>
                </div>
                {(fit?.aligned_strengths || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    {t('pages.talentMap.detail.noStrengths', { defaultValue: 'No strengths captured yet.' })}
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {fit.aligned_strengths.map((s, i) => (
                      <li key={i} className="flex gap-3 text-sm leading-relaxed">
                        <span className="h-5 w-5 rounded-full bg-success/15 text-success flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                        {typeof s === 'string' ? s : (s.label || s.competency_label || s.code || JSON.stringify(s))}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
              <Card className="p-6 shadow-card">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-9 w-9 rounded-lg bg-warning/10 text-warning flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5" />
                  </div>
                  <h3 className="font-display font-bold text-lg tracking-tight">
                    {t('pages.talentMap.developmentAreas', { defaultValue: 'Development areas' })}
                  </h3>
                </div>
                {(fit?.gaps || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    {t('pages.talentMap.detail.noGaps', { defaultValue: 'No development areas captured yet.' })}
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {fit.gaps.map((g, i) => (
                      <li key={i} className="flex gap-3 text-sm leading-relaxed">
                        <span className="h-5 w-5 rounded-full bg-warning/15 text-warning flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                        {typeof g === 'string' ? g : (g.label || g.competency_label || g.code || JSON.stringify(g))}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>

            {competencies.length > 0 && (
              <Card className="mt-6 p-6 shadow-card" data-testid="tm-detail-competencies">
                <h3 className="font-display font-bold text-lg tracking-tight mb-4">
                  {t('pages.talentMap.detail.competencies', { defaultValue: 'Competency breakdown' })}
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                        <th className="py-2 pr-3">{t('pages.talentMap.detail.competency', { defaultValue: 'Competency' })}</th>
                        <th className="py-2 px-3 text-center">{t('pages.talentMap.detail.score', { defaultValue: 'Score' })}</th>
                        <th className="py-2 px-3 text-center">{t('pages.talentMap.detail.band', { defaultValue: 'Band' })}</th>
                        <th className="py-2 pl-3 text-center">{t('pages.talentMap.detail.gap', { defaultValue: 'Gap' })}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {competencies.map((c, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-2 pr-3 font-medium">{c.label}</td>
                          <td className="py-2 px-3 text-center tabular-nums">{c.score == null ? '—' : FORMAT_SCORE(c.score)}</td>
                          <td className="py-2 px-3 text-center text-xs">{c.band || '—'}</td>
                          <td className="py-2 pl-3 text-center text-xs">{c.gap || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* --------------------------- COMPARE -------------------------- */}
          <TabsContent value="compare" className="mt-5">
            <Card className="p-6 shadow-card" data-testid="tm-detail-compare">
              <h3 className="font-display font-bold text-lg tracking-tight mb-2">
                {t('pages.talentMap.compatHeader', { defaultValue: 'Role compatibility' })}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('pages.talentMap.overallCompat', { defaultValue: 'Overall compatibility against role-profile targets.' })}
              </p>
              {compatibility == null ? (
                <p className="text-sm text-muted-foreground py-12 text-center italic">
                  {t('pages.talentMap.detail.noCompat', {
                    defaultValue: 'Compatibility score unavailable — no role profile attached or evaluation incomplete.',
                  })}
                </p>
              ) : (
                <div className="mt-6 flex flex-col md:flex-row gap-6 items-center">
                  <div className="relative h-44 w-44 shrink-0">
                    <svg className="w-full h-full -rotate-90">
                      <circle cx="50%" cy="50%" r="70" stroke="hsl(var(--muted))" strokeWidth="14" fill="none" />
                      <circle
                        cx="50%" cy="50%" r="70" stroke="hsl(var(--primary))" strokeWidth="14" fill="none"
                        strokeDasharray={`${Math.max(0, Math.min(100, compatibility)) * 4.4} 999`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className="font-display text-4xl font-bold">{FORMAT_SCORE(compatibility)}%</div>
                      <div className="text-xs text-muted-foreground mt-0.5 capitalize">
                        {(fit?.verdict || '').replace(/_/g, ' ') || t('pages.talentMap.strongMatch', { defaultValue: 'Match' })}
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 space-y-3 w-full">
                    {sections.map((d, i) => {
                      const delta = (d.target != null) ? d.score - d.target : null;
                      return (
                        <div key={i}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="font-medium">{d.label}</span>
                            {delta != null && (
                              <span className={delta >= 0 ? 'text-success font-medium' : 'text-warning font-medium'}>
                                {delta >= 0 ? '+' : ''}{delta}
                              </span>
                            )}
                          </div>
                          <div className="relative h-1.5 bg-muted rounded-full overflow-hidden">
                            {d.target != null && (
                              <div className="absolute h-full bg-accent/30 rounded-full" style={{ width: `${d.target}%` }} />
                            )}
                            <div className="absolute h-full bg-primary rounded-full" style={{ width: `${d.score}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>

        <SuiteDocSection suiteCode="talentmap" />
      </div>

      {/* Founder UAT F-01 — mandatory human-review attestation before release */}
      <AlertDialog open={confirmOpen} onOpenChange={(o) => { setConfirmOpen(o); if (!o) setAttested(false); }}>
        <AlertDialogContent data-testid="tm-release-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('pages.talentMap.releaseDialogTitle', { defaultValue: 'Release report to client' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('pages.talentMap.releaseDialogBody', {
                defaultValue: 'The report will become visible to the client organization. This action requires an explicit human review confirmation and is recorded in the audit trail.',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-start gap-3 rounded-md border border-border p-3 cursor-pointer">
            <Checkbox
              checked={attested}
              onCheckedChange={(v) => setAttested(!!v)}
              className="mt-0.5"
              data-testid="tm-release-attest-checkbox"
            />
            <span className="text-sm leading-relaxed">
              {t('pages.talentMap.releaseAttest', {
                defaultValue: 'I confirm that I have personally reviewed this report (human review).',
              })}
            </span>
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tm-release-cancel">
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={!attested || releasing}
              onClick={(e) => { e.preventDefault(); releaseReport(); }}
              data-testid="tm-release-confirm"
            >
              {releasing
                ? t('common.loading', { defaultValue: 'Loading…' })
                : t('pages.talentMap.releaseConfirm', { defaultValue: 'Confirm release' })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


// ---------------------------------------------------------------------------
// ENTRYPOINT
// ---------------------------------------------------------------------------


export default function AdminTalentMap() {
  const { t } = useTranslation();
  const { assignmentId } = useParams();
  if (assignmentId) return <TalentMapDetail t={t} assignmentId={assignmentId} />;
  return <TalentMapList t={t} />;
}
